import { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import payload from './generated/changelogs.json';

type ChangelogEntry = {
  version: string;
  date: string | null;
  body: string;
};

type ChangelogProject = {
  slug: string;
  name: string;
  description: string;
  changelogPath: string;
  intro: string;
  entries: ChangelogEntry[];
};

type ChangelogPayload = {
  generatedAt: string | null;
  projects: ChangelogProject[];
};

const data = payload as ChangelogPayload;

function formatDate(date: string | null) {
  if (!date) {
    return 'Unreleased';
  }

  const asDate = new Date(date);
  if (Number.isNaN(asDate.getTime())) {
    return date;
  }

  return asDate.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export default function App() {
  const [activeSlug, setActiveSlug] = useState(
    data.projects.length ? data.projects[0].slug : ''
  );

  const activeProject = useMemo(
    () => data.projects.find((project) => project.slug === activeSlug) ?? null,
    [activeSlug]
  );

  const generatedAt = data.generatedAt
    ? new Date(data.generatedAt).toLocaleString()
    : 'Not yet generated';

  return (
    <div className="layout">
      <aside className="sidebar">
        <h1>Titus Changelog</h1>
        <p>Release notes for Titus.</p>
        <p className="generated-at">Last generated: {generatedAt}</p>

        <ul className="project-list">
          {data.projects.map((project) => (
            <li key={project.slug}>
              <button
                type="button"
                className={`project-button${
                  project.slug === activeSlug ? ' active' : ''
                }`}
                onClick={() => setActiveSlug(project.slug)}
              >
                <strong>{project.name}</strong>
                <span>{project.description}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <main className="content">
        {!activeProject ? (
          <div className="empty-state">
            <h2>No changelog data available</h2>
            <p>
              Run <code>pnpm run build</code> after updating <code>CHANGELOG.md</code>
              to regenerate the site content.
            </p>
          </div>
        ) : (
          <>
            <header>
              <h2>{activeProject.name}</h2>
              <p>{activeProject.description}</p>
              <p className="generated-at">
                Source: <code>{activeProject.changelogPath}</code>
              </p>
            </header>

            {activeProject.intro && (
              <section className="entry-card markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                  {activeProject.intro}
                </ReactMarkdown>
              </section>
            )}

            <section className="entries">
              {activeProject.entries.map((entry) => (
                <article className="entry-card" key={`${activeProject.slug}-${entry.version}`}>
                  <h3>{entry.version}</h3>
                  <div className="entry-meta">{formatDate(entry.date)}</div>
                  <div className="markdown">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                      {entry.body || '_No notes for this release._'}
                    </ReactMarkdown>
                  </div>
                </article>
              ))}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
